// Template variable suggestions for autocomplete (Liquid syntax)
// Supports namespaces: params, details, variables, auth
// Supports filters: date_value, format_date, add_duration, url_encode, base64_encode, etc.

export const TEMPLATE_NAMESPACES = [
  {
    prefix: "params.",
    label: "params",
    description: "Request parameters",
    color: "text-blue-600 dark:text-blue-400",
    children: [
      { value: "params.start_date", label: "start_date", description: "Start date for events query" },
      { value: "params.end_date", label: "end_date", description: "End date for events query" },
      { value: "params.space_id", label: "space_id", description: "Space ID for filtering" }
    ]
  },
  {
    prefix: "details.",
    label: "details",
    description: "Linked account details",
    color: "text-green-600 dark:text-green-400",
    children: [
      { value: "details.api_key", label: "api_key", description: "API key from account details" },
      { value: "details.school_id", label: "school_id", description: "School ID from account" },
      { value: "details.tenant_id", label: "tenant_id", description: "Tenant ID from account" }
    ]
  },
  {
    prefix: "variables.",
    label: "variables",
    description: "Custom variables",
    color: "text-purple-600 dark:text-purple-400",
    children: [
      { value: "variables.", label: "(your custom variable)", description: "Access custom variables defined above" }
    ]
  },
  {
    prefix: "auth.",
    label: "auth",
    description: "Auth credentials (session auth only)",
    color: "text-orange-600 dark:text-orange-400",
    children: [
      { value: "auth.username", label: "username", description: "Username for session auth" },
      { value: "auth.password", label: "password", description: "Password for session auth" }
    ]
  },
  {
    prefix: "\"now\"",
    label: "\"now\"",
    description: "Current date/time (use with date filters)",
    color: "text-cyan-600 dark:text-cyan-400",
    children: [
      { value: "\"now\" | date_value", label: "\"now\" | date_value", description: "Current date/time" },
      { value: "\"now\" | date_value | format_date: \"YYYY-MM-DD\"", label: "now formatted", description: "Current date formatted" },
      { value: "\"now\" | date_value | format_date: \"ISO8601\"", label: "now ISO8601", description: "Current date in ISO8601" },
      { value: "\"now\" | date_value | add_duration: \"7d\"", label: "now + 7 days", description: "Add 7 days to now" },
      { value: "\"now\" | date_value | add_duration: \"-1M\"", label: "now - 1 month", description: "Subtract 1 month" },
      { value: "\"now\" | date_value | start_of: \"month\"", label: "start of month", description: "Start of current month" },
      { value: "\"now\" | date_value | end_of: \"month\"", label: "end of month", description: "End of current month" }
    ]
  }
]

// Liquid filters available for template expressions
export const TEMPLATE_FILTERS = [
  // Date filters
  { value: "date_value", description: "Convert to date/time", category: "date" },
  { value: "format_date: \"YYYY-MM-DD\"", description: "Format as YYYY-MM-DD", category: "date" },
  { value: "format_date: \"ISO8601\"", description: "Format as ISO8601", category: "date" },
  { value: "format_date: \"unix\"", description: "Format as Unix timestamp", category: "date" },
  { value: "add_duration: \"7d\"", description: "Add 7 days", category: "date" },
  { value: "add_duration: \"-1M\"", description: "Subtract 1 month", category: "date" },
  { value: "start_of: \"month\"", description: "Start of month", category: "date" },
  { value: "start_of: \"week\"", description: "Start of week", category: "date" },
  { value: "end_of: \"month\"", description: "End of month", category: "date" },
  { value: "end_of: \"year\"", description: "End of year", category: "date" },
  { value: "in_timezone: \"America/New_York\"", description: "Convert timezone", category: "date" },
  // Encoding filters
  { value: "url_encode", description: "URL encode (form encoding)", category: "encoding" },
  { value: "base64_encode", description: "Base64 encode", category: "encoding" },
  { value: "base64_decode", description: "Base64 decode", category: "encoding" },
  { value: "json_encode", description: "JSON encode", category: "encoding" },
  // Built-in Liquid filters
  { value: "upcase", description: "Convert to uppercase", category: "string" },
  { value: "downcase", description: "Convert to lowercase", category: "string" },
  { value: "strip", description: "Remove whitespace", category: "string" },
  { value: "default: \"value\"", description: "Default if empty", category: "string" }
]

// Date format options for format_date filter
export const DATE_FORMATS = [
  { value: "YYYY-MM-DD", description: "2024-01-15" },
  { value: "YYYYMMDD", description: "20240115" },
  { value: "ISO8601", description: "2024-01-15T10:30:00Z" },
  { value: "MM/DD/YYYY", description: "01/15/2024" },
  { value: "DD/MM/YYYY", description: "15/01/2024" },
  { value: "unix", description: "Unix timestamp (seconds)" },
  { value: "unix_ms", description: "Unix timestamp (milliseconds)" }
]

// Duration units for add_duration filter
export const DURATION_UNITS = [
  { value: "s", label: "seconds", example: "30s" },
  { value: "m", label: "minutes", example: "15m" },
  { value: "h", label: "hours", example: "2h" },
  { value: "d", label: "days", example: "7d" },
  { value: "w", label: "weeks", example: "2w" },
  { value: "M", label: "months", example: "1M" },
  { value: "y", label: "years", example: "1y" }
]

/**
 * Get autocomplete suggestions based on current input context
 * @param {string} text - Full input text
 * @param {number} cursorPos - Current cursor position
 * @returns {Object} { suggestions: Array, replaceStart: number, replaceEnd: number }
 */
export function getSuggestions(text, cursorPos) {
  // Find the template being typed (look backwards from cursor for {{)
  const textBeforeCursor = text.slice(0, cursorPos)
  const templateStartIndex = textBeforeCursor.lastIndexOf("{{")

  // Not inside a template
  if (templateStartIndex === -1) {
    return { suggestions: [], replaceStart: cursorPos, replaceEnd: cursorPos }
  }

  // Check if we're past a closing }}
  const textAfterTemplateStart = textBeforeCursor.slice(templateStartIndex)
  if (textAfterTemplateStart.includes("}}")) {
    return { suggestions: [], replaceStart: cursorPos, replaceEnd: cursorPos }
  }

  // Get the partial text being typed inside {{ }}
  const partialTemplate = textBeforeCursor.slice(templateStartIndex + 2)

  // Find where the template ends (next }} or end of string)
  const textAfterCursor = text.slice(cursorPos)
  const templateEndInRest = textAfterCursor.indexOf("}}")
  const templateEndIndex = templateEndInRest === -1 ? text.length : cursorPos + templateEndInRest

  // Check if we're after a pipe - suggest filters
  const lastPipeIndex = partialTemplate.lastIndexOf("|")
  if (lastPipeIndex !== -1) {
    const afterPipe = partialTemplate.slice(lastPipeIndex + 1).trim().toLowerCase()
    const filterSuggestions = generateFilterSuggestions(afterPipe)

    return {
      suggestions: filterSuggestions,
      replaceStart: templateStartIndex + 2 + lastPipeIndex + 1,
      replaceEnd: templateEndIndex
    }
  }

  // Generate variable suggestions
  const suggestions = generateSuggestions(partialTemplate)

  return {
    suggestions,
    replaceStart: templateStartIndex + 2,
    replaceEnd: templateEndIndex
  }
}

function generateFilterSuggestions(partial) {
  const trimmed = partial.trim().toLowerCase()

  return TEMPLATE_FILTERS
    .filter(f => f.value.toLowerCase().includes(trimmed) || trimmed === "")
    .map(f => ({
      value: " " + f.value,
      label: f.value,
      description: f.description,
      color: f.category === "date" ? "text-cyan-600 dark:text-cyan-400" :
             f.category === "encoding" ? "text-pink-600 dark:text-pink-400" :
             "text-gray-600 dark:text-gray-400",
      isFilter: true
    }))
    .slice(0, 10)
}

function generateSuggestions(partial) {
  const trimmed = partial.trim().toLowerCase()
  const suggestions = []

  // If empty or very short, show namespace suggestions
  if (trimmed.length < 2) {
    return TEMPLATE_NAMESPACES.map(ns => ({
      value: ns.prefix,
      label: ns.label,
      description: ns.description,
      color: ns.color,
      isNamespace: true
    }))
  }

  // Check if we're typing within a specific namespace
  for (const ns of TEMPLATE_NAMESPACES) {
    if (trimmed.startsWith(ns.prefix.toLowerCase()) || ns.prefix.toLowerCase().startsWith(trimmed)) {
      // If fully typed the namespace prefix, show children
      if (trimmed.startsWith(ns.prefix.toLowerCase())) {
        const afterPrefix = trimmed.slice(ns.prefix.length)
        for (const child of ns.children) {
          if (child.label.toLowerCase().includes(afterPrefix) || afterPrefix === "") {
            suggestions.push({
              value: child.value,
              label: child.label,
              description: child.description,
              color: ns.color
            })
          }
        }
      } else {
        // Still typing the namespace, suggest it
        suggestions.push({
          value: ns.prefix,
          label: ns.label,
          description: ns.description,
          color: ns.color,
          isNamespace: true
        })
      }
    }
  }

  // Also add fuzzy matches from all children
  if (suggestions.length === 0) {
    for (const ns of TEMPLATE_NAMESPACES) {
      for (const child of ns.children) {
        if (child.value.toLowerCase().includes(trimmed)) {
          suggestions.push({
            value: child.value,
            label: child.value,
            description: child.description,
            color: ns.color
          })
        }
      }
    }
  }

  return suggestions.slice(0, 10) // Limit to 10 suggestions
}

// Regex to match template variables for highlighting (with optional spaces)
export const TEMPLATE_PATTERN = /\{\{[^}]+\}\}/g

/**
 * Parse text and return segments with template highlighting info
 * @param {string} text
 * @returns {Array<{text: string, isTemplate: boolean}>}
 */
export function parseTemplateSegments(text) {
  if (!text) return []

  const segments = []
  let lastIndex = 0
  let match

  const regex = new RegExp(TEMPLATE_PATTERN.source, "g")

  while ((match = regex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      segments.push({ text: text.slice(lastIndex, match.index), isTemplate: false })
    }
    // Add the template match
    segments.push({ text: match[0], isTemplate: true })
    lastIndex = regex.lastIndex
  }

  // Add remaining text
  if (lastIndex < text.length) {
    segments.push({ text: text.slice(lastIndex), isTemplate: false })
  }

  return segments
}
